import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { api, srcForCover } from "@/lib/api";
import { formatDate, formatDuration } from "@/lib/utils2";
import { Play, Pause, Star, Headphones, CircleDot, StickyNote } from "lucide-react";
import { toast } from "sonner";
import EpisodeNotes from "@/components/EpisodeNotes";

export default function EpisodeRow({ episode, onFavoriteToggle }) {
  const { current, playing, toggle } = usePlayer();
  const isCurrent = current && current.id === episode.id;
  const isPlaying = isCurrent && playing;

  const fav = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/episodes/${episode.id}/favorite`);
      toast.success(data.favorite ? "Pinned to top of your list" : "Removed from favorites");
      onFavoriteToggle && onFavoriteToggle();
    } catch { toast.error("Could not update favorite."); }
  };

  return (
    <div
      data-testid={`episode-row-${episode.id}`}
      onClick={() => toggle(episode)}
      className="group flex items-center gap-4 p-3 sm:p-4 rounded-xl border border-transparent hover:border-brand-line hover:bg-white transition-colors duration-200 cursor-pointer"
    >
      <div className="relative h-14 w-14 shrink-0 rounded-lg overflow-hidden border border-brand-line">
        <img src={srcForCover(episode)} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/35 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {episode.favorite && <Star className="w-3.5 h-3.5 text-brand-dough fill-brand-dough shrink-0" />}
          <p className={`font-semibold truncate ${isCurrent ? "text-brand" : "text-brand-ink"}`}>{episode.title}</p>
        </div>
        <p className="text-sm text-brand-muted truncate">
          {episode.hosts?.join(" & ") || "The Spread"}
          {episode.season ? ` · S${episode.season}E${episode.episode_number}` : ""}
          {" · "}{formatDate(episode.publish_at)}
        </p>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {episode.listened ? (
          <span className="hidden sm:inline-flex items-center gap-1 text-xs text-brand" data-testid="episode-listened">
            <Headphones className="w-3.5 h-3.5" /> Listened
          </span>
        ) : (
          <span className="hidden sm:inline-flex items-center gap-1 text-xs text-brand-terracotta font-medium" data-testid="episode-new">
            <CircleDot className="w-3.5 h-3.5" /> New
          </span>
        )}
        <span className="text-xs tabular-nums text-brand-muted w-10 text-right">{formatDuration(episode.duration_seconds)}</span>
        <EpisodeNotes
          episode={episode}
          trigger={
            <button
              data-testid={`notes-button-${episode.id}`}
              onClick={(e) => e.stopPropagation()}
              className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-brand-sidebar transition-colors duration-200"
              title="My notes"
            >
              <StickyNote className="w-4 h-4 text-brand-muted" />
            </button>
          }
        />
        <button
          data-testid={`favorite-button-${episode.id}`}
          onClick={fav}
          className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-brand-sidebar transition-colors duration-200"
        >
          <Star className={`w-4 h-4 ${episode.favorite ? "text-brand-dough fill-brand-dough" : "text-brand-muted"}`} />
        </button>
      </div>
    </div>
  );
}
